/**
 * WHATSAPP FORM MODAL — Modale di invio messaggio WhatsApp da una scheda contatto.
 *
 * Aperto da PersonDetail (azione "WhatsApp"). Campi:
 *  - phoneNumber: select coi numeri del contatto (default: il principale)
 *  - body: testo del messaggio
 *
 * Al submit: dispatch sendWhatsAppMessage({personId, phoneNumber, body}) →
 * POST /whatsapp-messages/{groupId}/send. Il backend invia via Meta e, solo se
 * l'invio riesce, persiste il messaggio OUT legandolo al contatto (personId).
 */

import { useEffect, useState, type FormEvent, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { useWhatsAppComposer } from './useWhatsAppComposer';
import { useWhatsAppSender } from './useWhatsAppSender';
import { WhatsAppFileChip } from './WhatsAppFileChip';
import { WhatsAppPhoneSelect, WhatsAppSenderSelect } from './WhatsAppNumberFields';
import { WhatsAppTemplatePicker } from './WhatsAppTemplatePicker';
import { defaultWhatsAppPhone } from './whatsAppMessageTypes';
import type { Phone } from '../phone/phoneTypes';

interface WhatsAppFormModalProps {
  personId: number;
  phones: Phone[];
  onClose: () => void;
  onCreated?: () => void;
  /** Titolo al posto di «Invia messaggio WhatsApp». */
  title?: string;
  /**
   * Scelta del destinatario, fra mittente e numero (la «Nuova chat» della pagina WhatsApp,
   * FILO-37): finché non c'è un contatto `personId` è 0 e l'invio resta spento.
   */
  recipientPicker?: ReactNode;
  /** Campo che prende il focus a finestra aperta (`autoFocus` lo perde nel focus trap). */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

const FORM_ID = 'whatsapp-form';

export default function WhatsAppFormModal({
  personId,
  phones,
  onClose,
  onCreated,
  title,
  recipientPicker,
  initialFocusRef,
}: WhatsAppFormModalProps) {
  const { t } = useTranslation();
  const {
    file,
    inputProps,
    openFilePicker,
    clearFile,
    submitting,
    send,
    windowConflict,
    dismissWindowConflict,
    pendingBody,
    sendTemplate,
  } = useWhatsAppComposer();

  // Il principale, come nella chat della pagina WhatsApp; senza, il primo.
  const defaultNumber = defaultWhatsAppPhone(phones);

  const [phoneNumber, setPhoneNumber] = useState(defaultNumber);
  // Cambiato il destinatario (Nuova chat), la scelta del numero riparte da capo.
  useEffect(() => setPhoneNumber(defaultNumber), [defaultNumber, personId]);
  const [body, setBody] = useState('');

  const { numbers, sender: senderPhoneNumberId, setSender } = useWhatsAppSender();

  const canSubmit =
    personId > 0 &&
    phoneNumber.trim().length > 0 &&
    (body.trim().length > 0 || file !== null);

  function done() {
    onCreated?.();
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    // Se la finestra di Meta è chiusa il backend risponde sempre 409: `send` non fa
    // un toast d'errore ma valorizza windowConflict, e sotto compare il popup che
    // mostra il testo del template e chiede conferma.
    const ok = await send({
      personId,
      phoneNumber,
      body,
      phoneNumberId: senderPhoneNumberId || undefined,
    });
    if (!ok) return;

    done();
  }

  const footer = (
    <>
      <Button
        type="submit"
        form={FORM_ID}
        variant="contained"
        disabled={submitting || !canSubmit}
      >
        {t('whatsapp.send')}
      </Button>
      <Button type="button" onClick={onClose} variant="outlined">
        {t('common.cancel')}
      </Button>
    </>
  );

  if (windowConflict) {
    return (
      <WhatsAppTemplatePicker
        conflict={windowConflict}
        pendingBody={pendingBody()}
        submitting={submitting}
        onClose={dismissWindowConflict}
        onSend={(template, parameters) => {
          void sendTemplate(template, parameters).then((ok) => {
            if (ok) done();
          });
        }}
      />
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="app-dialog"
      slotProps={{ transition: { onEntered: () => initialFocusRef?.current?.focus() } }}
    >
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">{title ?? t('whatsapp.createTitle')}</span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <form id={FORM_ID} onSubmit={handleSubmit} className="whatsapp-form">
          <WhatsAppSenderSelect
            numbers={numbers}
            value={senderPhoneNumberId}
            onChange={setSender}
            fullWidth
            className="form-field"
          />

          {recipientPicker}

          <WhatsAppPhoneSelect
            phones={phones}
            value={phoneNumber}
            onChange={setPhoneNumber}
            required
            fullWidth
            // Senza contatto non c'è niente da scegliere; un contatto senza numeri lo si dice.
            disabled={phones.length === 0}
            helperText={personId > 0 && phones.length === 0 ? t('whatsapp.noPhone') : undefined}
            className="form-field"
          />

          <TextField
            label={t('whatsapp.message')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required={!file}
            fullWidth
            multiline
            minRows={4}
            className="form-field"
          />

          <div className="whatsapp-form__attach">
            <input {...inputProps} />
            <Button
              type="button"
              variant="outlined"
              size="small"
              startIcon={<AttachFileIcon fontSize="small" />}
              onClick={openFilePicker}
            >
              {t('whatsapp.attach')}
            </Button>
            {file && (
              <WhatsAppFileChip fileName={file.name} onRemove={clearFile} />
            )}
          </div>
        </form>
      </DialogContent>
      <DialogActions className="app-dialog__footer">{footer}</DialogActions>
    </Dialog>
  );
}
